import React, { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { chartTooltipProps, pieColor } from "../../constants/chartTheme";
import type { RevenueByTier } from "../../types";
import { formatINR } from "../../utils/format";

type Props = {
  tiers: RevenueByTier[];
  height?: number;
};

/** Ticket Tier Split by revenue, from the dashboard's chart_data.revenue_by_tier. */
export const TicketTierSplitPie: React.FC<Props> = ({ tiers, height = 220 }) => {
  // ``tier`` and ``tier_name`` are both optional on the wire; normalise to a
  // single label so the legend and keys are never undefined.
  const data = useMemo(
    () =>
      tiers
        .filter((t) => (t.revenue ?? 0) > 0)
        .map((t, index) => ({ ...t, label: t.tier || t.tier_name || `Tier ${index + 1}` })),
    [tiers]
  );
  const total = useMemo(() => data.reduce((sum, t) => sum + (t.revenue ?? 0), 0), [data]);

  if (data.length === 0) {
    return <p className="eventos-subtle">Add ticket tiers to see the revenue split.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="revenue"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius={80}
          stroke="none"
          isAnimationActive={false}
        >
          {data.map((tier, index) => (
            <Cell key={tier.label} fill={pieColor(index)} />
          ))}
        </Pie>
        <Tooltip
          {...chartTooltipProps}
          formatter={(value: number, _n: string, entry: { payload?: { label?: string } }) => {
            const share = total > 0 ? ` · ${Math.round((value / total) * 100)}%` : "";
            return [`${formatINR(value)}${share}`, entry.payload?.label ?? "Tier"];
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default TicketTierSplitPie;
